// The decoded, human-readable projection of a search-engine `SearchQuery`, produced by the
// backend (`domain::admin::search_joy::filters_display_from_params`). Two sources feed it and
// both land on this one shape:
//   - a tracked search   → `searchjoy.params_jsonb`
//   - a saved filter     → `UserFilters.filter`, flattened by `domain::admin::user_filter`
// so the panel renders both through the same component.

export type IndustryLabel = {
  slug: string;
  name: string;
};

export type FiltersDisplay = {
  // `queries[]` when present, else the single `q`; `*` and blanks dropped. Multiple phrases
  // are OR-ed by the engine (a tender matching ANY phrase is a hit).
  phrases: string[];
  excluded_phrases?: string[];
  // Industry slugs already translated to Polish by the backend.
  industries: IndustryLabel[];
  // One entry per CPV code — the backend splits values that pack several codes into one
  // string. Cascading: a code selects its whole subtree.
  cpv_code: string[];
  categories: string[];
  forms: string[];
  // Legacy taxonomy (`data.branches`), unrelated to CPV. The F5 migration emptied this for
  // saved filters, so a non-empty value here means the filter predates it.
  branches: string[];
  states: string[];
  districts: string[];
  cities: string[];
  countries: string[];
  organizers: string[];
  sources: string[];
  budget_min: number | null;
  budget_max: number | null;
  created_at_min: string | null;
  created_at_max: string | null;
  submitting_date_min: string | null;
  submitting_date_max: string | null;
};

// True when the decoded filter constrains nothing — an "empty" saved filter that would match
// the whole index.
export function isEmptyFiltersDisplay(filters: FiltersDisplay): boolean {
  return (
    filters.phrases.length === 0 &&
    (filters.excluded_phrases?.length ?? 0) === 0 &&
    filters.industries.length === 0 &&
    filters.cpv_code.length === 0 &&
    filters.categories.length === 0 &&
    filters.forms.length === 0 &&
    filters.branches.length === 0 &&
    filters.states.length === 0 &&
    filters.districts.length === 0 &&
    filters.cities.length === 0 &&
    filters.countries.length === 0 &&
    filters.organizers.length === 0 &&
    filters.sources.length === 0 &&
    filters.budget_min === null &&
    filters.budget_max === null &&
    filters.created_at_min === null &&
    filters.created_at_max === null &&
    filters.submitting_date_min === null &&
    filters.submitting_date_max === null
  );
}
